<template>
  <ul ref="layersContainerRef" data-testid="layers-container-ref" class="overflow-hidden">
    <li
      v-for="layer in layers"
      :key="isTextLabel(layer) ? layer.id : layer.nodeId"
      class="layer"
      :class="{ collapsed: collapsed[layer.nodeId] }"
    >
      <EditorDeckSlidesThreeDLabelLayer
        v-if="isTextLabel(layer)"
        :readonly="readonly"
        :label="layer"
        :labels-manager="labelsManager"
        :selected-in-panel="isSelected(layer)"
        :selected-in-scene="selectedLabelId === layer.id"
        :scoping-element="scopingElement"
        :is-editing-individual-scene="isEditingIndividualScene"
        @set-visible="!disabled && $emit('set-layer-visible', [{ labelId: layer.id, visible: $event }])"
        @click.left="$emit('click', $event, layer)"
        @click.right="clickIfNotSelected($event, layer)"
        @contextmenu.stop.prevent="$emit('contextmenu', $event, getSelectionFor(layer))"
        @vrify-dragstart="handleDragStart"
        @vrify-dragend="handleDragEnd"
      />
      <EditorDeckSlidesThreeDLayerListItem
        v-else
        class="group h-9"
        :selected-in-panel="isSelected(layer)"
        :visible="layer.visible"
        :data-testid="`layer-${layer.nodeId}`"
        @set-visible="!disabled && $emit('set-layer-visible', [{ nodeId: layer.nodeId, visible: $event }])"
        @click.left="$emit('click', $event, layer)"
        @click.right="clickIfNotSelected($event, layer)"
        @mouseleave="$emit('layer-hover', { nodeId: layer.nodeId, hover: false })"
        @mouseover="$emit('layer-hover', { nodeId: layer.nodeId, hover: true })"
        @contextmenu.stop.prevent="!disabled && $emit('contextmenu', $event, getSelectionFor(layer))"
      >
        <div class="flex items-center flex-1 w-full truncate layer-title" :class="{ 'pointer-events-none': disabled }">
          <TypeIcon v-if="type === 'drill'" class="flex-shrink-0 w-3 h-3 mr-2 text-copper-200" data-testid="label-type-icon" />
          <ThreeDIcon v-else class="w-3 h-3 mr-2 text-copper-200" />
          <span :title="isTextLabel(layer) ? layer.content : layer.title" class="truncate max-w-4/5">{{
            isTextLabel(layer) ? layer.content : layer.title
          }}</span>
        </div>
      </EditorDeckSlidesThreeDLayerListItem>
    </li>
  </ul>
</template>

<script lang="ts" setup>
import type { DeepReadonly, PropType } from 'vue'
import TypeIcon from '@/components/icons/TypeIcon.svg'
import ThreeDIcon from '@/components/icons/ThreeDSlideIcon.svg'
import { isTextLabel, selectableId } from '@/lib/three-d-editor/utils'
import type { SceneElement } from '@/entities/model3d'
import { equals } from 'ramda'
import type { LabelsManager, TextLabel } from '~/lib/three-d-resources/labels-manager'
import type { LabelLayer } from '~/composables/useThreeDLayerGroups'

const props = defineProps({
  layers: {
    type: Array as PropType<LabelLayer[]>,
    required: true,
  },
  selectedLayers: {
    type: Array as PropType<DeepReadonly<ThreeDElement[]>>,
    required: true,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  labelsManager: {
    type: Object as PropType<LabelsManager>,
    required: true,
  },
  scopingElement: {
    type: Object as PropType<SceneElement | null>,
    default: null,
  },
  isEditingIndividualScene: {
    type: Boolean,
    default: false,
  },
  selectedLabelId: {
    type: String as PropType<string | null>,
    default: null,
  },
  type: {
    type: String as PropType<string | null>,
    default: null,
  },
})

const emit = defineEmits(['layer-hover', 'set-layer-visible', 'click', 'contextmenu', 'vrify-dragstart', 'vrify-dragend'])

const layersContainerRef = ref<HTMLElement | null>(null)
const collapsed = ref<{ [key: number]: boolean }>({})

const selectedLayerIds = computed(() => props.selectedLayers.map(l => selectableId(l as ThreeDElement)))

function isSelected(layer: Layer | TextLabel) {
  const id = selectableId(layer)
  return selectedLayerIds.value.includes(id)
}

function getSelectionFor(layer: Layer | TextLabel): ThreeDElement | ThreeDElement[] {
  const prevSelectedLayers = selectedLayerIds.value
  const layerSelectableId = selectableId(layer)
  const wasPreviouslySelected = prevSelectedLayers.includes(layerSelectableId)

  if ((prevSelectedLayers.length > 1 && wasPreviouslySelected) || (isTextLabel(layer) && wasPreviouslySelected)) {
    return props.selectedLayers as ThreeDElement[]
  }

  return layer
}

function clickIfNotSelected(event: MouseEvent, element: Layer | TextLabel): void {
  if (!isSelected(element)) {
    emit('click', event, element)
  }
}

function handleDragStart(e: string) {
  emit('vrify-dragstart', e)
}

function handleDragEnd() {
  emit('vrify-dragend')
}

watch(
  () => props.selectedLayers,
  (layers, oldLayers) => {
    if (!equals(layers, oldLayers)) {
      _scrollSelectedElementIntoView('.selected-in-panel')
    }
  },
  { flush: 'post' }
)

watch(
  () => props.selectedLabelId,
  selectedLabel => {
    if (selectedLabel) {
      _scrollSelectedElementIntoView('.selected-in-scene')
    }
  },
  { flush: 'post' }
)

function _scrollSelectedElementIntoView(elementClassName: string) {
  if (props.selectedLayers.length === 1 || props.selectedLabelId) {
    layersContainerRef.value?.querySelector(elementClassName)?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    })
  }
}
</script>

<style lang="stylus" scoped>
.layer-title
  @apply cursor-default select-none m-0 ml-6
  // These 4 for for no text wrap and ellipsis overflow
  @apply min-w-0 whitespace-nowrap overflow-hidden overflow-ellipsis

.layer.collapsed
  .layer-labels
    @apply max-h-0 overflow-hidden
  .close-icon
    @apply transform -rotate-90

:deep(.controls-container .control-button)
  padding calc(0.75rem - 1px) // Modify padding to have button size match slide buttons
  @apply border-0
</style>
